import { BigNumber } from '@frontend/numeric';

import { ICompanyExposure } from '@aqx-core/common/api/dealer/model';

import {
  EPositionType,
  ICompanyExposurePositionMap,
  ICompanyExposureRowData,
  IDisplayCompanyExposureAggregated,
  IDisplayCompanyExposureSingle
} from './types';

export const mapApiPositionTypeToEnum = (apiPositionType: number | string): EPositionType => {
  if (apiPositionType === 0 || apiPositionType === '0') {
    return EPositionType.BUY;
  }
  if (apiPositionType === 1 || apiPositionType === '1') {
    return EPositionType.SELL;
  }
  return apiPositionType as EPositionType;
};

export const transformCompanyExposureFromApi = (companyExposure: ICompanyExposure[]): ICompanyExposure[] => {
  return companyExposure.map((exposure) => {
    const positionType = mapApiPositionTypeToEnum(exposure.positionType as number | string);
    return {
      ...exposure,
      totalExposure: calculateTotalExposure(exposure.latestPrice, exposure.units, exposure.exchangeRate),
      positionType,
      profit: calculateProfitLoss(
        positionType,
        exposure.latestPrice,
        exposure.openPrice,
        exposure.lotSize,
        exposure.exchangeRate
      )
    };
  });
};

export const calculateNetPosition = (
  buyPosition: ICompanyExposure,
  sellPosition: ICompanyExposure
): ICompanyExposure => {
  const netUnits = new BigNumber(buyPosition.units).minus(sellPosition.units).toNumber();
  const netLotSize = new BigNumber(buyPosition.lotSize).minus(sellPosition.lotSize).toNumber();
  const netVolume = buyPosition.volume - sellPosition.volume;
  const positionType = netLotSize >= 0 ? EPositionType.BUY : EPositionType.SELL;
  const netOpenPrice =
    (buyPosition.openPrice * buyPosition.units - sellPosition.openPrice * sellPosition.units) / netUnits;

  const latestPrice = buyPosition.latestPrice || sellPosition.latestPrice;

  const exchangeRate = buyPosition.exchangeRate || sellPosition.exchangeRate || 1;

  const netProfit = new BigNumber(latestPrice)
    .minus(netOpenPrice)
    .multipliedBy(netLotSize)
    .multipliedBy(exchangeRate)
    .toNumber();

  const totalExposure = calculateTotalExposure(latestPrice, Math.abs(netUnits), exchangeRate);

  return {
    ...buyPosition,
    positionType,
    volume: netVolume,
    lotSize: netLotSize,
    units: netUnits,
    openPrice: netOpenPrice,
    latestPrice: latestPrice,
    latestTradeTime: Math.max(buyPosition.latestTradeTime, sellPosition.latestTradeTime),
    profit: netProfit,
    totalExposure: totalExposure,
    exchangeRate: exchangeRate,
    positionCount: buyPosition.positionCount + sellPosition.positionCount
  };
};

export const buildSymbolMapFromCompanyExposure = (companyExposure: ICompanyExposure[]) => {
  const symbolMap = new Map<ICompanyExposure['symbol'], ICompanyExposurePositionMap>();

  companyExposure.forEach((position) => {
    try {
      const currentSymbolValue = symbolMap.get(position.symbol);

      if (!currentSymbolValue) {
        symbolMap.set(position.symbol, {
          [position.positionType === EPositionType.BUY ? 'buyPosition' : 'sellPosition']: position
        });
        return;
      }

      let { buyPosition, sellPosition } = currentSymbolValue;

      if (position.positionType === EPositionType.BUY) {
        buyPosition = position;
      } else {
        sellPosition = position;
      }

      if (!buyPosition || !sellPosition) {
        throw new Error(`Invalid company exposure for symbol: ${position.symbol}`);
      }

      symbolMap.set(position.symbol, {
        buyPosition,
        sellPosition,
        netPosition: calculateNetPosition(buyPosition, sellPosition)
      });
    } catch {}
  });

  return symbolMap;
};

export const mapSymbolToDisplayCompanyExposure = (
  symbolMap: Map<ICompanyExposure['symbol'], ICompanyExposurePositionMap>
): ICompanyExposureRowData[] => {
  return Array.from(symbolMap.values()).map((item) => {
    if (!item.netPosition) {
      const position = item.buyPosition ?? item.sellPosition;
      if (!position) {
        throw new Error('Invalid company exposure data: no position found');
      }
      return {
        type: 'single',
        symbol: position.symbol || '',
        position: position
      } as IDisplayCompanyExposureSingle;
    } else {
      if (!item.buyPosition || !item.sellPosition) {
        throw new Error('Invalid company exposure data: missing buy or sell position');
      }
      return {
        type: 'aggregated',
        symbol: item.netPosition.symbol,
        netPosition: item.netPosition,
        buyPosition: item.buyPosition,
        sellPosition: item.sellPosition
      } as IDisplayCompanyExposureAggregated;
    }
  });
};

export const filterCompanyExposureBySearch = (
  companyExposure: ICompanyExposureRowData[],
  keyword?: string
): ICompanyExposureRowData[] => {
  if (!keyword) {
    return companyExposure;
  }

  return companyExposure.filter(({ symbol }) => symbol.toLowerCase().includes(keyword.toLowerCase()));
};

export const calculateTotalExposure = (currentPrice: number, units: number, exchangeRate = 1): number => {
  return new BigNumber(currentPrice).multipliedBy(units).multipliedBy(exchangeRate).toNumber();
};

export const calculateProfitLoss = (
  positionType: EPositionType,
  currentPrice: number,
  openPrice: number,
  lotSize: number,
  exchangeRate = 1
): number => {
  return positionType === EPositionType.BUY
    ? new BigNumber(currentPrice).minus(openPrice).multipliedBy(lotSize).multipliedBy(exchangeRate).toNumber()
    : new BigNumber(openPrice).minus(currentPrice).multipliedBy(lotSize).multipliedBy(exchangeRate).toNumber();
};
